import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useReactionAnalytics, getVibeMessage } from '@/hooks/useReactionAnalytics';
import { REACTION_TYPES } from './ReactionPicker';
import { BarChart3, Loader2, TrendingUp, MessageSquare } from 'lucide-react';
import { cn } from '@/lib/utils';

interface WallReactionAnalyticsProps {
  profileId: string;
}

const getReactionEmoji = (reactionType: string): string => {
  const reaction = REACTION_TYPES.find(r => r.id === reactionType);
  return reaction?.emoji || '❓';
};

const getReactionLabel = (reactionType: string): string => {
  const reaction = REACTION_TYPES.find(r => r.id === reactionType);
  return reaction?.label || reactionType;
};

const ReactionBar: React.FC<{ 
  reactionType: string; 
  count: number; 
  percentage: number; 
  maxPercentage: number;
}> = ({ reactionType, count, percentage, maxPercentage }) => {
  const barWidth = maxPercentage > 0 ? (percentage / maxPercentage) * 100 : 0;
  
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="text-xl w-8">{getReactionEmoji(reactionType)}</span>
      <div className="flex-1">
        <div className="h-4 bg-muted rounded-full overflow-hidden">
          <div 
            className="h-full bg-primary/70 rounded-full transition-all duration-500"
            style={{ width: `${barWidth}%` }}
          />
        </div>
      </div>
      <span className="w-12 text-right text-muted-foreground">{percentage}%</span>
      <span className="w-10 text-right font-medium">{count}</span>
    </div>
  );
};

const WeeklyChart: React.FC<{ data: { week_start: string; count: number }[] }> = ({ data }) => {
  const maxCount = Math.max(...data.map(d => d.count), 1);
  
  const formatWeekLabel = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="flex items-end justify-between gap-2 h-24">
      {data.map((week, i) => (
        <div key={week.week_start} className="flex-1 flex flex-col items-center gap-1">
          <div className="w-full bg-muted rounded-t relative" style={{ height: '80px' }}>
            <div 
              className={cn(
                "absolute bottom-0 left-0 right-0 rounded-t transition-all duration-500",
                i === data.length - 1 ? "bg-primary" : "bg-primary/60"
              )}
              style={{ height: `${(week.count / maxCount) * 100}%`, minHeight: week.count > 0 ? '4px' : '0' }}
            />
          </div>
          <span className="text-[10px] text-muted-foreground">{formatWeekLabel(week.week_start)}</span>
          <span className="text-xs font-medium">{week.count}</span>
        </div>
      ))}
    </div>
  );
};

const WallReactionAnalytics: React.FC<WallReactionAnalyticsProps> = ({ profileId }) => {
  const { analytics, isLoading, error } = useReactionAnalytics(profileId);

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1.5">
          <BarChart3 className="h-4 w-4" />
          <span className="hidden sm:inline">My Stats</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Your Reaction Vibe
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="text-center py-8 text-destructive">
            <p>{error}</p>
          </div>
        ) : !analytics ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>No analytics available yet</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Top Vibe This Month */}
            {analytics.topReceivedVibe && (
              <div className="text-center py-4 bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 rounded-xl">
                <p className="text-sm text-muted-foreground mb-2">Your Top Vibe This Month</p>
                <div className="text-5xl mb-2">{getReactionEmoji(analytics.topReceivedVibe.reaction_type)}</div>
                <p className="text-lg font-bold text-primary">
                  {getVibeMessage(analytics.topReceivedVibe.reaction_type)}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {analytics.topReceivedVibe.count} {getReactionLabel(analytics.topReceivedVibe.reaction_type)} reactions received
                </p>
              </div>
            )}

            {/* Reactions Received Breakdown */}
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Reactions Received ({analytics.totalReceived})
              </h3>
              {analytics.receivedBreakdown.length > 0 ? (
                <div className="space-y-2">
                  {analytics.receivedBreakdown.slice(0, 6).map(item => (
                    <ReactionBar
                      key={item.reaction_type}
                      reactionType={item.reaction_type}
                      count={item.count}
                      percentage={item.percentage}
                      maxPercentage={analytics.receivedBreakdown[0]?.percentage || 100}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No reactions received yet. Keep posting! 🚀
                </p>
              )}
            </div>

            {/* Reactions Given Breakdown */}
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <span className="text-lg">🎁</span>
                Reactions Given ({analytics.totalGiven})
              </h3>
              {analytics.givenBreakdown.length > 0 ? (
                <div className="space-y-2">
                  {analytics.givenBreakdown.slice(0, 6).map(item => (
                    <ReactionBar
                      key={item.reaction_type}
                      reactionType={item.reaction_type}
                      count={item.count}
                      percentage={item.percentage}
                      maxPercentage={analytics.givenBreakdown[0]?.percentage || 100}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Start giving reactions to support other athletes! 🤝
                </p>
              )}
            </div>

            {/* Weekly Trend */}
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Weekly Reactions Received
              </h3>
              <WeeklyChart data={analytics.weeklyTrend} />
            </div>

            {/* Most Reacted Post */}
            {analytics.mostReactedPost && (
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Your Most Popular Post
                </h3>
                <div className="p-4 bg-muted/50 rounded-lg border">
                  <p className="text-sm line-clamp-3">
                    {analytics.mostReactedPost.content || `[${analytics.mostReactedPost.post_type} post]`}
                  </p>
                  <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                    <span className="font-medium text-primary">
                      {analytics.mostReactedPost.total_reactions} reactions
                    </span>
                    <span>•</span>
                    <span>
                      {new Date(analytics.mostReactedPost.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WallReactionAnalytics;
